import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Charge } from './types/planTypes';
import { updateChargeMutation } from '@/graphql/mutations/updateCharge';
import { getChargeTypeDisplay } from './utils/planDisplayUtils';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectAllPricingMetrics, selectPricingMetricName, selectPricingMetricById } from '@/store/selectors/metricsSelectors';
import { updateChargeInRedux } from '@/store/actions/chargeActions';
import { X, Search, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface EditChargeModalProps {
  charge: Charge | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedCharge: Charge) => void;
}

interface PricingMetric {
  id: string;
  name?: string;
  display_name?: string;
  modality?: string;
}

const CURRENCY_OPTIONS = [
  { value: '$', label: '$ (USD)' },
  { value: '€', label: '€ (EUR)' },
  { value: '£', label: '£ (GBP)' },
  { value: '¥', label: '¥ (JPY)' },
  { value: '%', label: '% (Percentage)' }
];

const CHARGE_TYPE_OPTIONS = [
  { value: 'monthly_recurring_billed_yearly', label: 'Yearly billing' },
  { value: 'monthly_recurring_billed_monthly', label: 'Monthly billing' },
  { value: 'pricing_not_disclosed', label: 'Contact for pricing' },
  { value: 'usage', label: 'Usage-based' },
  { value: 'freemium', label: 'Free' }
];

const EditChargeModal: React.FC<EditChargeModalProps> = ({
  charge,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { toast } = useToast();
  const [price, setPrice] = useState<string>('');
  const [currency, setCurrency] = useState<string>('$');
  const [pricePer, setPricePer] = useState<string>('');
  const [chargeType, setChargeType] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedMetricId, setSelectedMetricId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showMetricSearch, setShowMetricSearch] = useState(false);
  
  // Get all metrics from Redux store
  const allMetrics = useAppSelector(selectAllPricingMetrics);
  
  // Filter metrics to only include those with allowed modalities
  const allowedModalities = ['Flat', 'License', 'Credit', 'Usage'];
  const eligibleMetrics = allMetrics.filter((metric: any) => 
    metric.modality && allowedModalities.includes(metric.modality)
  );
  
  // Get current metric name if available
  const currentMetricName = useAppSelector(state => 
    selectPricingMetricName(state, charge?.pricing_metric_id || null)
  );
  
  // Initialize form when charge data is available
  useEffect(() => {
    if (charge) {
      setPrice(charge.price?.toString() || '');
      setCurrency(charge.currency || '$');
      setPricePer(charge.price_per || charge.per || '');
      setChargeType(charge.type || '');
      setSelectedMetricId(charge.pricing_metric_id || null);
    }
  }, [charge]);
  
  // Filter metrics based on search term
  const filteredMetrics = searchTerm.trim() === '' 
    ? eligibleMetrics 
    : eligibleMetrics.filter(metric => {
        const name = metric.display_name || metric.name || metric.id;
        return name.toLowerCase().includes(searchTerm.toLowerCase());
      });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!charge?.id) {
      toast({
        title: "Error",
        description: "Charge ID is missing",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      console.log('Submitting charge update:', { id: charge.id, input: {
        price: price ? parseFloat(price) : null,
        currency,
        price_per: pricePer,
        type: chargeType,
        pricing_metric_id: selectedMetricId
      }});
      
      // Update the charge using GraphQL mutation
      const response = await updateChargeMutation(
        charge.id,
        {
          price: price ? parseFloat(price) : null,
          currency,
          price_per: pricePer, // Only use price_per, 'per' column doesn't exist
          type: chargeType,
          pricing_metric_id: selectedMetricId
        }
      );
      
      // Log the response for debugging
      console.log('GraphQL update response:', JSON.stringify(response, null, 2));
      
      // Check if response has the expected structure
      if (!response || !response.updateCharge) {
        throw new Error('Invalid response format from GraphQL server');
      }
      
      const updatedCharge = response.updateCharge;

      // Update the Redux store to immediately reflect changes in UI
      updateChargeInRedux(updatedCharge);

      // Show success message
      toast({
        title: "Success",
        description: "Charge updated successfully"
      });
      
      // Call the onSuccess prop with the updated charge data
      if (onSuccess) {
        onSuccess(updatedCharge);
      }
      
      // Use the global refresh function if available to refresh the UI immediately
      const refreshCompanyData = (window as any).refreshCompanyData;
      if (typeof refreshCompanyData === 'function') {
        console.log("Calling parent refreshCompanyData function after charge update");
        refreshCompanyData();
      }
      
      onClose();
    } catch (error: any) {
      // Extract the most useful error message
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.errors && error.response.errors.length > 0) {
        // Extract GraphQL errors
        errorMessage = error.response.errors.map((e: any) => e.message).join('; ');
        console.log('GraphQL error details:', error.response.errors);
      } else if (error.error) {
        // Extract Supabase error message
        errorMessage = `Database error: ${error.error.message || 'Unknown database error'}`;
        console.log('Supabase error details:', error.error);
      } else if (error.message) {
        // Use the standard error message
        errorMessage = error.message;
      }
      
      console.error('❌ [EditChargeModal] Error:', error);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Edit {charge ? getChargeTypeDisplay(charge.type) : 'Charge'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label htmlFor="chargeType">Charge Type</Label>
            <select
              id="chargeType"
              value={chargeType}
              onChange={(e) => setChargeType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mb-4"
            >
              <option value="">Select charge type</option>
              {CHARGE_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <Label htmlFor="metric" className="mb-1 block">Associated Metric</Label>
            
            {!showMetricSearch ? (
              <div className="flex items-center space-x-2">
                <div 
                  className="flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {selectedMetricId ? (
                    <div className="flex justify-between w-full items-center">
                      <span>{currentMetricName || selectedMetricId}</span>
                      <button 
                        type="button" 
                        onClick={() => setSelectedMetricId(null)}
                        className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">No metric associated</span>
                  )}
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowMetricSearch(true)}>
                  Change
                </Button>
              </div>
            ) : (
              <div className="border rounded-md p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search metrics..."
                      className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowMetricSearch(false)}>
                    Cancel
                  </Button>
                </div>
                
                <Alert className="mb-3 bg-blue-50 border-blue-100">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-xs">
                    Only metrics with modality of Flat, License, Credit, or Usage can be associated with charges.
                  </AlertDescription>
                </Alert>
                
                <div className="max-h-40 overflow-y-auto border rounded-md">
                  {filteredMetrics.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">
                      {searchTerm ? 'No matching metrics found' : 'No eligible metrics available'}
                    </div>
                  ) : (
                    <div>
                      <div 
                        className="p-2 border-b hover:bg-gray-50 cursor-pointer" 
                        onClick={() => {
                          setSelectedMetricId(null);
                          setShowMetricSearch(false);
                          setSearchTerm('');
                        }}>
                        <span className="text-gray-600">None (Remove association)</span>
                      </div>
                      {filteredMetrics.map(metric => (
                        <div 
                          key={metric.id} 
                          className="p-2 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer"
                          onClick={() => {
                            setSelectedMetricId(metric.id);
                            setShowMetricSearch(false);
                            setSearchTerm('');
                          }}>
                          <div className="flex flex-col">
                            <span>{metric.display_name || metric.name || metric.id}</span>
                            <span className="text-xs text-gray-500">
                              Modality: {metric.modality} | ID: {metric.id}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-end gap-2">
            <div className="w-1/4">
              <Label htmlFor="currency">Currency</Label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {CURRENCY_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex-1">
              <Label htmlFor="price">Price</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Enter price"
                min="0"
                disabled={chargeType === 'pricing_not_disclosed'}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="pricePer">Price per</Label>
            <Input
              id="pricePer"
              value={pricePer}
              onChange={(e) => setPricePer(e.target.value)}
              placeholder="e.g., month, user, request"
            />
          </div>

          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditChargeModal;
